// AppShell (SPEC §4.1) — раскладка desktop(SplitPane: список ↔ контент) / mobile(StackPane: стек+назад)
// поверх react-router. Левая панель — список чатов (всегда виден на desktop), правая/верхняя —
// активный маршрут (чат/поиск/настройки/админка). Здесь же: badge суммарных непрочитанных в pwa,
// in-app-вотчер, тост-стек, оффлайн-баннер, модальный релогин по auth.required (кеш цел).
//
// Адаптив через useLayout(): смена брейкпойнта НЕ теряет выбранный чат (маршрут общий) — лишь
// перекладывает его в нужный примитив (инв.3).

import { useEffect } from 'react'
import { Routes, Route, useLocation } from 'react-router-dom'
import { SplitPane, StackPane, useLayout } from '@tele-chat/design-system'
import { useSelector } from '../store/StoreContext.js'
import { usePwa } from '../pwa/PwaContext.js'
import { useNotifications } from '../notifications/NotificationsContext.js'
import { useInAppNotifications } from '../notifications/useInAppNotifications.js'
import { ConnBanner } from '../components/ConnBanner.js'
import { ToastStack } from '../components/ToastStack.js'
import { AuthScreen } from '../screens/AuthScreen.js'
import { ChatListScreen } from '../screens/ChatListScreen.js'
import { ConversationScreen } from '../screens/ConversationScreen.js'
import { GroupInfoScreen } from '../screens/GroupInfoScreen.js'
import { SearchScreen } from '../screens/SearchScreen.js'
import { NewChatScreen } from '../screens/NewChatScreen.js'
import { SettingsScreen } from '../screens/SettingsScreen.js'
import { AdminScreen } from '../screens/AdminScreen.js'
import { EmptyState } from '../components/EmptyState.js'
import { strings } from '../i18n/strings.js'

/** Контент правой/верхней панели по текущему маршруту. */
function RoutedContent(): JSX.Element {
  return (
    <Routes>
      <Route path="/" element={<EmptyContent />} />
      <Route path="/new" element={<NewChatScreen />} />
      <Route path="/chat/:chatId" element={<ConversationScreen />} />
      <Route path="/chat/:chatId/profile" element={<GroupInfoScreen />} />
      <Route path="/search" element={<SearchScreen />} />
      <Route path="/settings/*" element={<SettingsScreen />} />
      <Route path="/admin/*" element={<AdminScreen />} />
      <Route path="*" element={<EmptyContent />} />
    </Routes>
  )
}

function EmptyContent(): JSX.Element {
  return <EmptyState title={strings.empty.selectChat} />
}

export function AppShell(): JSX.Element {
  const layout = useLayout()
  const location = useLocation()
  const pwa = usePwa()
  const { notify } = useNotifications()

  // Badge суммарных непрочитанных (FR-DELIVERY-03) → pwa.setBadge на каждое изменение.
  const totalUnread = useSelector((s) => s.select.totalUnread())
  useEffect(() => {
    void pwa.pwa?.setBadge(totalUnread)
  }, [totalUnread, pwa.pwa])

  // Тост «доступно обновление» → applyUpdate.
  useEffect(() => {
    if (pwa.updateReady && pwa.pwa) {
      notify({
        kind: 'update',
        title: strings.notif.updateAvailable,
        action: { label: strings.conn.reload, run: () => pwa.pwa?.applyUpdate() },
        dedupeKey: 'sw-update', // один тост обновления, не стопка (защита + стабильный notify рвёт петлю)
      })
    }
  }, [pwa.updateReady, pwa.pwa, notify])

  // Модальный релогин по auth.required (кеш/маршрут целы, §4.6).
  const needRelogin = useSelector((s) => s.select.needRelogin())

  // Вотчер in-app тостов (новое входящее в не-активном чате).
  useInAppNotifications()

  // На desktop правая панель — текущий маршрут; список всегда слева.
  // На mobile стек: список + (если выбран маршрут не «/») верхний экран; «назад» = navigate(-1)/'/'.
  const isRootRoute = location.pathname === '/'

  const list = <ChatListScreen />
  const content = <RoutedContent />

  return (
    <div data-app-shell="" data-layout={layout} style={{ height: '100%', position: 'relative' }}>
      <ConnBanner />

      {layout === 'desktop' ? (
        <SplitPane start={list} end={content} />
      ) : (
        <StackPane panes={isRootRoute ? [list] : [list, content]} />
      )}

      <ToastStack />

      {needRelogin ? <AuthScreen relogin /> : null}
    </div>
  )
}
